export interface DropdownPanelPosition {
  opensAbove: boolean;
  styles: Record<string, string>;
}

export type DropdownBoundary = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>;

const DEFAULT_MAX_HEIGHT = 232;
const DROPDOWN_ITEM_HEIGHT = 34;
const DROPDOWN_MENU_CHROME = 12;
const DIALOG_BOUNDARY_SELECTOR = [
  '.mat-mdc-dialog-surface',
  '.mdc-dialog__surface',
  '.modal-content',
  '[role="dialog"]',
].join(', ');

export function estimateDropdownContentHeight(
  itemCount: number,
  maxHeight = DEFAULT_MAX_HEIGHT,
): number {
  return Math.min(
    maxHeight,
    Math.max(DROPDOWN_ITEM_HEIGHT, itemCount * DROPDOWN_ITEM_HEIGHT + DROPDOWN_MENU_CHROME),
  );
}

export function findDropdownBoundaryRect(from: Element | null | undefined): DOMRect {
  if (from) {
    const boundary = from.closest(DIALOG_BOUNDARY_SELECTOR);
    if (boundary instanceof HTMLElement) {
      return boundary.getBoundingClientRect();
    }
  }

  return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

export function computeDropdownPanelPosition(
  triggerRect: DOMRect,
  options?: {
    maxHeight?: number;
    contentHeight?: number;
    gap?: number;
    viewportPadding?: number;
    zIndex?: number;
    boundary?: DropdownBoundary;
  },
): DropdownPanelPosition {
  const maxHeight = options?.maxHeight ?? DEFAULT_MAX_HEIGHT;
  const contentHeight = Math.min(
    maxHeight,
    options?.contentHeight ?? maxHeight,
  );
  const gap = options?.gap ?? 5;
  const viewportPadding = options?.viewportPadding ?? 8;
  const zIndex = options?.zIndex ?? 1300;
  const box = resolveVisibleBoundary(options?.boundary, viewportPadding);

  const spaceBelow = box.bottom - triggerRect.bottom;
  const spaceAbove = triggerRect.top - box.top;
  const neededBelow = contentHeight + gap;
  const opensAbove = spaceBelow < neededBelow && spaceAbove > spaceBelow;
  const availableSpace = Math.max(0, (opensAbove ? spaceAbove : spaceBelow) - gap);
  const panelMaxHeight = Math.min(maxHeight, availableSpace);

  const styles: Record<string, string> = {
    position: 'fixed',
    left: `${triggerRect.left}px`,
    width: `${triggerRect.width}px`,
    maxHeight: `${panelMaxHeight}px`,
    zIndex: `${zIndex}`,
  };

  if (opensAbove) {
    styles.bottom = `${window.innerHeight - triggerRect.top + gap}px`;
  } else {
    styles.top = `${triggerRect.bottom + gap}px`;
  }

  return { opensAbove, styles };
}

function resolveVisibleBoundary(
  boundary: DropdownBoundary | undefined,
  padding: number,
): DropdownBoundary {
  const viewport = {
    top: padding,
    right: window.innerWidth - padding,
    bottom: window.innerHeight - padding,
    left: padding,
  };

  if (!boundary) {
    return viewport;
  }

  return {
    top: Math.max(boundary.top + padding, viewport.top),
    right: Math.min(boundary.right - padding, viewport.right),
    bottom: Math.min(boundary.bottom - padding, viewport.bottom),
    left: Math.max(boundary.left + padding, viewport.left),
  };
}
